import { Code, ConnectError } from '@connectrpc/connect'
import {
  PlantUnitErrorReason,
  PlantUnitErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'
import { mapMasterDeleteError } from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'
import { powerTypeIntegrityMessage } from '~/pages/AdminOcctoMaster/tabs/_common/models/powerTypeIntegrity'
import { PENDING_SITE_ID } from '~/pages/AdminPlantUnitMaster/models/unitForm'

/** 電源ユニットの登録・更新・削除・取得の失敗文言の prefix (code ごとの文言を引く)。 */
export const PLANT_UNIT_ERROR_KEY_PREFIX =
  'page:adminPlantUnitMaster.unit.serverError'

export const PLANT_UNIT_UNKNOWN_ERROR_KEY = `${PLANT_UNIT_ERROR_KEY_PREFIX}.unknown`

/** 登録・更新で、code ごとに引く既定の文言 (`useCrudModalForm.customErrorKeys`)。 */
export const PLANT_UNIT_SAVE_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.Aborted]: `${PLANT_UNIT_ERROR_KEY_PREFIX}.aborted`,
  [Code.NotFound]: `${PLANT_UNIT_ERROR_KEY_PREFIX}.notFound`,
  [Code.InvalidArgument]: `${PLANT_UNIT_ERROR_KEY_PREFIX}.invalidArgument`,
}

/**
 * `PlantUnitErrorReason` → i18n key の registry。管理対象のユニットの削除拒否は
 * 操作全体の失敗 (欄に紐づかない)。
 */
const PLANT_UNIT_REASON_KEYS: Record<
  PlantUnitErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [PlantUnitErrorReason.UNSPECIFIED]: undefined,
  [PlantUnitErrorReason.MANAGED]: {
    key: `${PLANT_UNIT_ERROR_KEY_PREFIX}.managed`,
  },
  // 電源種別の整合の拒否 (保存で返る) は型つきの detail から文言を作る (powerTypeIntegrityMessage)。
  // 削除では返らないので、ここでは文言を引かない。
  [PlantUnitErrorReason.POWER_TYPE_MIX]: undefined,
  [PlantUnitErrorReason.LAST_UNIT_OF_BG_PLANT]: undefined,
  [PlantUnitErrorReason.BG_POWER_TYPE_MIX]: undefined,
  [PlantUnitErrorReason.POWER_TYPE_CHANGE_NEEDS_CONFIRMATION]: undefined,
  [PlantUnitErrorReason.PLAN_CATEGORY_MISMATCH]: undefined,
}

/**
 * 登録・更新の AlreadyExists の出し先を決める文言。
 *
 * - 既存の発電所へのユニット: 同じ発電所に同じユニット名がある → ユニット名の欄
 * - その場で追加した発電所: 発電所の名称が登録済み → 発電所の欄
 *   (新しい発電所を付けた登録では、ユニット名は衝突しようがない)
 *
 * それ以外の失敗は undefined を返し、code ごとの既定文言に任せる。
 */
export function resolvePlantUnitSaveError(
  err: ConnectError,
  t: TFunction,
  context: {
    /** 保存しようとしたユニット名 (trim 済み)。 */
    unitName: string
    /** 選んだ発電所の id (仮 id なら追加した発電所)。 */
    siteId: string
    /** 発電所の名称 (選んだ発電所、または追加した発電所)。 */
    siteName: string
  },
): AppErrorInfoResolution | undefined {
  if (err.code === Code.FailedPrecondition) {
    // 電源種別の整合 (期間の重なり・最後のユニット・BG の混在・計画区分) で止めた理由。欄に紐づかない
    const message = powerTypeIntegrityMessage(t, err)
    return message === undefined ? undefined : { kind: 'operation', message }
  }
  if (err.code !== Code.AlreadyExists) {
    return undefined
  }
  if (context.siteId === PENDING_SITE_ID) {
    return {
      kind: 'field',
      field: 'siteId',
      message: t(`${PLANT_UNIT_ERROR_KEY_PREFIX}.duplicateSiteName`, {
        name: context.siteName,
      }),
    }
  }
  return {
    kind: 'field',
    field: 'unitName',
    message: t(`${PLANT_UNIT_ERROR_KEY_PREFIX}.duplicateUnitName`, {
      site: context.siteName,
      name: context.unitName,
    }),
  }
}

/**
 * 削除の失敗文言。管理対象のユニット (FailedPrecondition + reason) は理由の文言を出し、
 * それ以外は他のマスタの削除と同じ code ごとの文言にする。
 */
export function mapPlantUnitDeleteError(t: TFunction, err: unknown): string {
  if (err instanceof ConnectError && err.code === Code.FailedPrecondition) {
    // E4: 発電 BG に所属する系統コードの最後のユニットは削除できない (型つき detail から文言を作る)
    const integrity = powerTypeIntegrityMessage(t, err)
    if (integrity !== undefined) {
      return integrity
    }
    const resolved = resolveAppErrorInfoMessage(
      err,
      PlantUnitErrorReasonSchema,
      PLANT_UNIT_REASON_KEYS,
      t,
    )
    if (resolved !== undefined) {
      return resolved.message
    }
  }
  return mapMasterDeleteError(t, err, PLANT_UNIT_ERROR_KEY_PREFIX)
}
